import { useState } from 'react';
import { api } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import { dateTimeLabel, relativeTime } from '../../lib/format';
import { EmptyState, LoadingPage, Stat, Tag } from '../../components/ui';
import { AdminNav, DayRange, SparkBars } from './AdminNav';
import { PageHeader } from '../../components/PageHeader';

export function VisitorAnalyticsPage() {
  const [days, setDays] = useState(30);
  const state = useAsync(async () => api.adminVisitorAnalytics(days), [days]);

  if (state.loading && !state.data) return <LoadingPage label="Loading visitor analytics…" />;
  const data = state.data;

  return (
    <div>
      <PageHeader
        title="Visitor analytics"
        lede="Everyone who opens hoaone.app, signed in or not. Geography comes from the request IP (Netlify when available, otherwise a lookup). Super Admin only."
      />
      <AdminNav />
      <DayRange value={days} onChange={setDays} />

      {state.error && <p className="mut">Could not load visitor analytics.</p>}
      {!data ? null : data.totals.visits === 0 ? (
        <EmptyState
          title="No visits in this range"
          hint="Open the public sign-in page from another browser to record the first visit."
        />
      ) : (
        <>
          <div className="card">
            <div className="stat-grid">
              <Stat label="Unique visitors" value={data.totals.visitors} />
              <Stat label="Visits" value={data.totals.visits} />
              <Stat label="New visitors" value={data.totals.newVisitors} />
              <Stat label="Returning visits" value={data.totals.returningVisits} />
              <Stat label="Signed-in visits" value={data.totals.signedInVisits} />
            </div>
            <SparkBars
              points={data.byDay.map((d) => ({ day: d.day, value: d.visitors }))}
              label="Unique visitors by day"
            />
          </div>

          <div className="split-2">
            <Breakdown title="Countries" rows={data.byCountry.map((r) => ({ label: r.countryName, n: r.visitors }))} />
            <Breakdown title="Regions" rows={data.byRegion.map((r) => ({ label: `${r.region}, ${r.countryName}`, n: r.visitors }))} />
            <Breakdown title="Cities" rows={data.byCity.map((r) => ({ label: [r.city, r.region, r.countryName].filter(Boolean).join(', '), n: r.visitors }))} />
            <Breakdown title="Devices" rows={data.byDevice.map((r) => ({ label: r.deviceType, n: r.visitors }))} />
            <Breakdown title="Browsers" rows={data.byBrowser.map((r) => ({ label: r.browser, n: r.visitors }))} />
            <Breakdown title="Operating systems" rows={data.byOs.map((r) => ({ label: r.os, n: r.visitors }))} />
          </div>

          <div className="card">
            <h3>Recent visits</h3>
            <div className="tw">
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Visitor</th>
                    <th>Location</th>
                    <th>Device</th>
                    <th>IP</th>
                    <th>Page</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.map((row, index) => (
                    <tr key={`${row.occurredAt}-${index}`}>
                      <td>
                        {dateTimeLabel(row.occurredAt)}
                        <div className="mut">{relativeTime(row.occurredAt)}</div>
                      </td>
                      <td>
                        <Tag tone={row.isReturning ? 'info' : 'ok'}>
                          {row.isReturning ? `Returning · ${row.sessionCount} sessions` : 'New'}
                        </Tag>
                        <div>
                          {row.userName ? (
                            <>
                              {row.userName}
                              <div className="mut">{row.userEmail}</div>
                            </>
                          ) : (
                            <span className="mut">Anonymous</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {[row.city, row.region, row.countryName].filter(Boolean).join(', ') || 'Unknown'}
                        {row.timezone && <div className="mut">{row.timezone}</div>}
                        {row.latitude != null && row.longitude != null && (
                          <div className="mut">
                            {row.latitude.toFixed(2)}, {row.longitude.toFixed(2)}
                          </div>
                        )}
                      </td>
                      <td>
                        {row.deviceType}
                        <div className="mut">
                          {[row.os, row.browser].filter(Boolean).join(' · ')}
                          {row.screen ? ` · ${row.screen}` : ''}
                        </div>
                      </td>
                      <td className="num" style={{ textAlign: 'left' }}>
                        {row.ip ?? '—'}
                      </td>
                      <td>
                        {row.path}
                        {row.referrer && <div className="mut">from {row.referrer}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Breakdown({ title, rows }: { title: string; rows: Array<{ label: string; n: number }> }) {
  const max = Math.max(...rows.map((r) => r.n), 1);
  return (
    <div className="card">
      <h3>{title}</h3>
      {rows.length === 0 ? (
        <p className="mut">None yet.</p>
      ) : (
        rows.map((row) => (
          <div key={row.label} style={{ marginBottom: 8 }}>
            <div className="row" style={{ justifyContent: 'space-between', fontSize: 13 }}>
              <span>{row.label}</span>
              <span className="mut">{row.n}</span>
            </div>
            <div className="bar-track">
              <div className="bar" style={{ width: `${(row.n / max) * 100}%` }} />
            </div>
          </div>
        ))
      )}
    </div>
  );
}
